<template>
  <div class="card modal-overflow edit-job" style="box-shadow: none;">
    <div class="card-body new-job">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label>Job Name</label>
            <div class="job-name-container grey-font">
              <h6>
                {{ ptracJob.name }}
                <span v-if="ptracJob.actualCompletionDate">
                  - Completed on: {{ ptracJob.actualCompletionDate | formatDate }}
                </span>
              </h6>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>
              Job Number
            </label>
            <el-input
              size="large"
              name="jobNumber"
              :disabled="true"
              class="job-number"
              v-model="ptracJob.number"
              placeholder="Job Number">
            </el-input>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Date Received</label>
            <el-input disabled :value="ptracJob.dateReceived | formatDate"/>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <div class="row">
            <div class="col-md-6">
              <span>Requested Date</span>
              <div class="card" style="padding:10px;">
                  {{ purchaseOrder.header.startDate | formatDate }}
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Target Completion Date</label>
                <el-date-picker
                  :disabled="!canEdit"
                  name="targetCompletionDate"
                  data-vv-as="target completion date"
                  :format="constants.DATE.FORMAT.DISPLAY"
                  :value-format="constants.DATE.FORMAT.VALUE"
                  :clearable=false
                  v-validate="modelValidations.targetCompletionDate"
                  type="date"
                  v-model="newTargetCompletionDate"
                  @input="dataChanged"
                  :picker-options="datePickerConfig"
                  placeholder="Pick a day">
                </el-date-picker>
                <small class="text-danger" v-show="errors.has('targetCompletionDate')">
                  {{ getError('targetCompletionDate') }}
                </small>
              </div>
            </div>
          </div>
          <div class="row" style="justify-content:space-between">
            <div class="col-md-12" v-if="!requestedChangeMade">
              <small  class="text-danger" style="display: block">
                Selected date does not match request!&nbsp&nbsp&nbsp
                <label for="override">Override?&nbsp</label>
                <input type="checkbox" id="override" class="inline-block" v-model="overrideRequestedChange"/>
              </small>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <auditable-entity :data="ptracJob"></auditable-entity>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <button @click="maybeChangeTaskDueDates"
                    class="btn btn-fill btn-info btn-wd btn-save pull-right"
                    v-if="canEdit"
                    :disabled="changesSaved || (!requestedChangeMade && !overrideRequestedChange)"
                    >
              Save Job
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Constants from 'src/utilities/constants'
import Permissions from 'src/utilities/permissions'
import {formatDate, notifyError, notifySuccess, utcToLocal,} from 'src/utilities/utils'
import AuditableEntity from 'src/components/UIComponents/AuditableEntity'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import ckEditorConfig from 'src/utilities/ckeditor'
import datePickerConfig from 'src/components/UIComponents/DatePicker/datePickerConfig'
import {OK} from 'http-status-codes'

import {sync} from 'vuex-pathify'

export default {
    props: ['jobId', 'purchaseOrder'],
    components: {
      AuditableEntity,
      ckeditor: CKEditor.component
    },
    computed: {
      ptracJob: sync('supplyPro/job/ptracJob'),
      canEdit() {
        if (this.$store.get('security/isInGroup', Constants.GROUPS.SUPER_USERS)) {
          return true
        }
        if (this.$store.get('supplyPro/job/ptracJob@actualCompletionDate')) {
          return false
        }
        return this.$can('edit', Permissions.JOBS)
      },
      canEditTaskDueDates() {
        return this.$can('edit', Permissions.TASK_DUE_DATE)
      },
      requestedChangeMade() {
        return formatDate(this.newTargetCompletionDate) === formatDate(this.purchaseOrder.header.startDate)
      },
      requestedChangeSaved() {
        return (this.requestedChangeMade || this.overrideRequestedChange) && this.changesSaved
      }
    },
    data() {
      return {
        editor: ClassicEditor,
        ckEditorConfig,
        changesSaved: true,
        constants: Constants,
        datePickerConfig,
        newTargetCompletionDate: null,
        originalTargetCompletionDate: null,
        overrideRequestedChange: false,
        tasks: [],
        modelValidations: {
          targetCompletionDate: {
            required: true
          }
        },
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      validate() {
        return this.$validator.validateAll()
      },
      getLabel(obj) {
        if (obj != null && obj.hasOwnProperty('name')) {
          return obj.name
        }
        return obj
      },
      maybeChangeTaskDueDates() {
        if (this.canEditTaskDueDates) {
          this.$eventHub.$emit('show-modal-3', 'Modify Task Due Dates', {jobId: this.jobId, dueDate: this.newTargetCompletionDate}, '500px')
        }
        this.validateAndSubmit()
      },
      dataChanged() {
        this.changesSaved = false
        this.overrideRequestedChange = false
      },
      async validateAndSubmit() {
        this.$store.set('supplyPro/job/ptracJob@targetCompletionDate', this.newTargetCompletionDate)
        if (await this.$validator.validateAll()) {
          const savePayload = this.$store.get('supplyPro/job/savePayload')
          const response = await this.$store.dispatch('save', savePayload)

          if (response.result === Constants.RETURN_CODES.SUCCESS) {
            notifySuccess(this, 'Job saved successfully')
            this.originalTargetCompletionDate = this.newTargetCompletionDate
            this.changesSaved = true
          } else {
            notifyError(this, 'Failed to Update Job', response.errors)
          }
        }
      },
    },
    watch: {
      requestedChangeSaved(newValue) {
          this.$emit('requestedJobChanged', newValue)
      },
    },
    async mounted() {
      await this.$store.cache.dispatch('statuses/fetchByEntityType', 'Job')

      const response = await this.$store.dispatch('fetchById', {endpoint: 'jobs', id: this.jobId})
      if (OK !== response.status) {
        notifyError(this, 'Failed to load Job by Id: ' + this.jobId)
        return
      }
      const job = response.data

      // dates coming from ptrac-api are a GMT date string
      job.dateReceived = utcToLocal(job.dateReceived)
      job.targetCompletionDate = utcToLocal(job.targetCompletionDate)

      this.$store.set('supplyPro/job/ptracJob', job)
      this.originalTargetCompletionDate = job.targetCompletionDate
      this.newTargetCompletionDate = job.targetCompletionDate
    },
    beforeDestroy() {
      this.$store.commit('supplyPro/job/reset')
    }
  }
</script>

<style scoped>
  .edit-job >>> #autocomplete-client .v-autocomplete-list, #autocomplete-builder .v-autocomplete-list {
    min-width: 280px;
    margin-top: 12px;
  }
  .edit-job >>> .job-name-container {
    background-color: lightgrey;
    padding: 11px 0 4px 8px;
    border-radius: 5px;
  }
  .edit-job >>> .tiny-label-button {
    padding: 1px 5px;
    margin-top: -1px;
    float: right;
  }
</style>
